import { TRAVELLERS_TYPE_LABELS } from '@/constants/common.constants';
import { INBOUND, TRAVELLERS_TYPES_MAP } from '@tajawal/web-flights-sdk/lib/constants';
import { PaxType } from '@tajawal/web-flights-sdk/lib/types/common.types';
import _isEmpty from 'lodash/isEmpty';

export function generateTravellerFormTestIDs(travellerType: PaxType, displayIndex: number) {
  const typeLabel = TRAVELLERS_TYPE_LABELS[travellerType];
  const travellerTestId = `${typeLabel}${displayIndex}`;
  const testIdPrefix = `FlightPAX__${travellerTestId}`;

  return {
    container: `FlightPAX__Traveller__${travellerTestId}`,
    type: `${testIdPrefix}__Header__PassengerTypeLabel`,
    displayIndex: `${testIdPrefix}__Header__PassengerNumberLabel`,
    infantInLabInfoIcon: `${testIdPrefix}__Header__InfoIcon`,
    title: `${testIdPrefix}__TitleInput`,
    titleMenu: `${testIdPrefix}__TitleMenu`,
    Mr:
      travellerType === TRAVELLERS_TYPES_MAP.ADULT.code
        ? `${testIdPrefix}__TitleSelector__OptionMr`
        : `${testIdPrefix}__TitleSelector__OptionMaster`,
    Ms:
      travellerType === TRAVELLERS_TYPES_MAP.ADULT.code
        ? `${testIdPrefix}__TitleSelector__OptionMs`
        : `${testIdPrefix}__TitleSelector__OptionMiss`,
    Mrs: `${testIdPrefix}__TitleSelector__OptionMrs`,
    selectedTitle: `${testIdPrefix}_selectedTitle`,
    titleError: `${testIdPrefix}__TitleSelectorErrorLabel`,
    firstName: `${testIdPrefix}__FirstNameInput`,
    firstNameError: `${testIdPrefix}__FirstNameErrorLabel`,
    middleName: `${testIdPrefix}__MiddleNameInput`,
    middleNameError: `${testIdPrefix}__MiddleNameErrorLabel`,
    lastName: `${testIdPrefix}__LastNameInput`,
    lastNameError: `${testIdPrefix}__LastNameErrorLabel`,
    dateOfBirthDay: `${testIdPrefix}__DOB__DaySelector`,
    dateOfBirthMonth: `${testIdPrefix}__DOB__MonthSelector`,
    dateOfBirthYear: `${testIdPrefix}__DOB__YearSelector`,
    dateOfBirthHijri: `${testIdPrefix}__DOB__HijriCheckBox`,
    dateOfBirthError: `${testIdPrefix}__DOBErrorLabel`,

    nationalityError: `${testIdPrefix}__NationalityErrorLabel`,
    passportOption: `${testIdPrefix}__DocumentTypeSelector__OptionPassport`,
    nationalIdOption: `${testIdPrefix}__DocumentTypeSelector__OptionNationalID`,
    idNumber: `${testIdPrefix}__DocumentNumberInput`,
    idNumberError: `${testIdPrefix}__DocumentNumberErrorLabel`,
    issuingCountry: `${testIdPrefix}__DocumentCountrySelector`,
    issuingCountryInput: `${testIdPrefix}__DocumentCountrySearchInput`,
    issuingCountryError: `${testIdPrefix}__DocumentCountryErrorLabel`,
    nationality: `${testIdPrefix}__NationalitySelector`,
    nationalityInput: `${testIdPrefix}__NationalitySearchInput`,
    idExpiryDateDay: `${testIdPrefix}__DocumentExpiry__DaySelector`,
    idExpiryDateMonth: `${testIdPrefix}__DocumentExpiry__MonthSelector`,
    idExpiryDateYear: `${testIdPrefix}__DocumentExpiry__YearSelector`,
    idExpiryDateHijri: `${testIdPrefix}__DocumentExpiry__HijriCheckBox`,
    idExpiryDateError: `${testIdPrefix}__DocumentExpiryErrorLabel`,

    documentType: `${testIdPrefix}__DTSelector`,
    documentTypeMenu: `${testIdPrefix}__documentTypeMenu`,
    selectedDocumenttype: `${testIdPrefix}_selectedDocumentType`,
    passport: `${testIdPrefix}__DT_PP`,
    iqamaId: `${testIdPrefix}__DT_II`,
    nationalId: `${testIdPrefix}__DT_NI`,

    advancedOptionsPrefix: travellerTestId,
  };
}

export function generateContactFormTestIDs() {
  const testIdPrefix = `FlightPAX__ContactDetails`;

  return {
    title: `${testIdPrefix}__TitleInput`,
    titleMenu: `${testIdPrefix}__TitleMenu`,
    Mr: `${testIdPrefix}__TitleSelector__OptionMr`,
    Ms: `${testIdPrefix}__TitleSelector__OptionMs`,
    Mrs: `${testIdPrefix}__TitleSelector__OptionMrs`,
    selectedTitle: `${testIdPrefix}_selectedTitle`,
    titleError: `${testIdPrefix}__TitleSelectorErrorLabel`,
    firstName: `${testIdPrefix}__FirstNameInput`,
    firstNameError: `${testIdPrefix}__FirstNameErrorLabel`,
    middleName: `${testIdPrefix}__MiddleNameInput`,
    middleNameError: `${testIdPrefix}__MiddleNameErrorLabel`,
    lastName: `${testIdPrefix}__LastNameInput`,
    lastNameError: `${testIdPrefix}__LastNameErrorLabel`,
    email: `${testIdPrefix}__EmailInput`,
    emailError: `${testIdPrefix}__EmailErrorLabel`,
    countryCode: `${testIdPrefix}__MobileNumberCountryCodeSelector`,
    phone: `${testIdPrefix}__MobileNumberInput`,
    phoneError: `${testIdPrefix}__MobileNumberErrorLabel`,
  };
}

const getFlightDetailsLabel = (legIndex: number) => {
  return legIndex === 0 ? 'Departure' : 'Return';
};

export const FlightDetailsTestIDs = {
  getTestIds: (): Record<string, string> => {
    return {
      closeBtnTestId: 'ReviewTrip__Close__Button',
      viewDetailBtnTestId: 'ReviewTrip__ViewDetail__Button',
      viewDetailCloseBtnTestId: 'ReviewTrip__ViewDetail__CloseButton',
      stickyBarFareTestId: 'ReviewTrip__Stickybar__Fare',
      stickyBarFareTaxesId: 'ReviewTrip__Stickybar__Taxes__Fees',
      bottomBarPriceTestId: 'ReviewTrip__BottomBar__Price',
      bottomBarBtnTestId: 'ReviewTrip__BottomBar__Continue__Button',
    };
  },

  getLegTestIds: (legIndex: number): Record<string, string> => {
    const label = getFlightDetailsLabel(legIndex);
    return {
      labelTestId: `ReviewTrip__${label}__TripTitleLabel`,
      flightTitleTestId: `ReviewTrip__${label}__Leg__FlightTitle`,
      flightDurationTestId: `ReviewTrip__${label}__Leg__FlightDuration`,
      flightDateTestId: `ReviewTrip__${label}__Leg__FlightDate`,
      flightTimeTestId: `ReviewTrip__${label}__Leg__FlightTime`,
      flightBaggageTestId: `ReviewTrip__${label}__Leg__Baggage`,
    };
  },

  getSegmentTestIds: (legIndex: number, segmentIndex: number): Record<string, string> => {
    const label = getFlightDetailsLabel(legIndex);
    const testIdPrefix = `ReviewTrip__${label}__Leg__Segment${segmentIndex}`;
    return {
      airlineLogoTestId: `${testIdPrefix}__AirlineLogo`,
      airlineLabelTestId: `${testIdPrefix}__AirlineLabel`,
      flightNumberTestId: `${testIdPrefix}__FlightNumberLabel`,
      aircraftTypeTestId: `${testIdPrefix}__AircraftTypeLabel`,
      cabinTypeTestId: `${testIdPrefix}__CabinTypeLabel`,
      durationTestId: `${testIdPrefix}__Duration`,
      waitTimeTestId: `${testIdPrefix}__WaitTime`,
      cabinBaggageTestId: `${testIdPrefix}__CabinBaggageValue`,
      checkedBaggageTestId: `${testIdPrefix}__CheckedBaggageValue`,
      basePrefix: testIdPrefix,
      expandButtonTestId: `${testIdPrefix}__ExpandButton`,
    };
  },

  getSegmentAirportTestIds: (basePrefix: string, segmentType: string): Record<string, string> => {
    return {
      dateTestId: `${basePrefix}__${segmentType}__Date`,
      timeTestId: `${basePrefix}__${segmentType}__Time`,
      terminalTestId: `${basePrefix}__${segmentType}__TerminalLabel`,
      airportTestId: `${basePrefix}__${segmentType}__AirportNameLabel`,
      cityTestId: `${basePrefix}__${segmentType}__CityLabel`,
    };
  },

  getToggleBtnTestId: (togglePriceDetails: boolean): string => {
    const state = togglePriceDetails ? 'Opened' : 'Closed';
    return `ReviewTrip__Stickybar__Button__${state}`;
  },
};

export function generateGroupingResultCardTestIDs({
  groupIndex,
  direction,
  optionNo,
  isTopPick = false,
  isGroupOption = false,
  isDesktop = false,
}) {
  const topPicks = isTopPick ? 'TopPick__' : '';
  const containerName = isDesktop ? 'Group' : 'FlightCard';
  const prefix = `${topPicks}${containerName}${isDesktop ? '_' : '__'}`;
  return {
    itinerary: `${prefix}${groupIndex}`,
    card: isGroupOption
      ? `FlightOption__container__${groupIndex}`
      : `${prefix}${isDesktop ? 'c' : 'C'}ontainer${isDesktop ? '_' : '__'}${groupIndex}`,
    baggageLabel: `${prefix}${groupIndex}__BaggageIncludedIcon`,
    baggageOptions: `${prefix}${groupIndex}__BaggageOptions`,
    currency: `${prefix}${groupIndex}__CurrencyLabel`,
    startsFrom: `${prefix}${groupIndex}__StartFromLabel`,
    price: `${prefix}${groupIndex}__PriceLabel`,
    discount: `${prefix}${groupIndex}__DiscountLabel`,
    oldTotal: `${prefix}${groupIndex}__PriceSlashLabel`,
    leg: `${prefix}${groupIndex}__${direction}Leg__Option${optionNo}`,
    airportChangeWarning: `${prefix}${groupIndex}__Warning_Airport_Change`,
    mixedCabinWarning: `${prefix}${groupIndex}__Warning_Mixed_Cabin_Class`,
    hajjUmrahWarning: `${prefix}${groupIndex}__Warning_Hajj_Umrah`,
    timeOptions: `${prefix}${groupIndex}__TimeOptionsLabel`,
    checkPrices: `Group${groupIndex}__CheckPricesButton`,
    selectFlight: `${prefix}${groupIndex}__SelectFlight`,
    flightDetails: `${prefix}${groupIndex}__FlightDetails`,
    hajjAndUmrah: `${prefix}${groupIndex}__Warning_Hajj_And_Umrah`,
    priceHint: `${prefix}${groupIndex}__PriceHint`,
  };
}

export const getSearchResultsLegTestIds = (
  itineraryGroupIndex: number,
  legIndex: number,
  isGroupingCard: boolean,
  isTopPick: boolean,
  isDesktop = false,
  isDomestic = false,
  direction = ''
) => {
  let legType = legIndex === 1 ? 'return' : 'departure';
  if (isDomestic) {
    legType = direction === INBOUND ? 'return' : 'departure';
  }
  const topPicks = isTopPick ? 'TopPick' : '';
  const containerName = isDesktop ? 'Group' : 'FlightOption__';
  const topPicksCardName = _isEmpty(topPicks) && isDesktop ? `Group` : '__FlightCard';
  const prefix = isGroupingCard ? containerName : `${topPicks}${topPicksCardName}`;
  const legOption = isDesktop ? 'Leg__Option0' : '__Leg';

  return {
    legTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}`,
    airlineLogoTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__AirlineLogo`,
    depTimeTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__DepartureTimeLabel`,
    depAirportTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__DepartureAirportLabel`,
    stopsLabelTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__StopsLabel`,
    durationLabelTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__DurationLabel`,
    arTimeTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__ArrivalTimeLabel`,
    arAirportTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__ArrivalAirportLabel`,
    arlineLabelTestId: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__AirlineLabel`,
    baggageIcon: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__BaggageIcon`,
    radioButton: `${prefix}${itineraryGroupIndex}__${legType}${legOption}__RadioButton`,
    timeOptions: `${prefix}${itineraryGroupIndex}__TimeOptions`,
  };
};

export function generateStopFilterTestID(code, isActive) {
  const enabled = isActive ? 'enabled' : 'disabled';
  switch (code) {
    case 0:
      return `direct_flights_only_stops_filter_${enabled}`;
    case 1:
      return `one_stop_or_less_stops_filter_${enabled}`;
    case 2:
      return `two_stops_or_less_stops_filter_${enabled}`;
    case 'all':
      return 'any_number_of_stops_stops_filter';
    default:
      return '';
  }
}
